import type { RefObject } from 'react';
import { useLayoutEffect, useRef, useState } from 'react';

const props: (keyof DOMRect)[] = [
	'bottom',
	'height',
	'left',
	'right',
	'top',
	'width',
];

function rectChanged(a: DOMRect = {} as DOMRect, b: DOMRect = {} as DOMRect) {
	return props.some((prop) => a[prop] !== b[prop]);
}

interface ObserverState {
	rect?: DOMRect;
	callbacks: ((rect: DOMRect) => void)[];
}

const observedNodes = new Map<Element, ObserverState>();
let rafId: number;

function run() {
	for (const [node, state] of observedNodes.entries()) {
		const newRect = node.getBoundingClientRect();
		if (rectChanged(newRect, state.rect)) {
			state.rect = newRect;
			for (const cb of state.callbacks) {
				cb(state.rect);
			}
		}
	}

	rafId = window.requestAnimationFrame(run);
}

function observeRect(node: Element, cb: (rect: DOMRect) => void) {
	const wasEmpty = observedNodes.size === 0;

	const state = observedNodes.get(node);
	if (state) {
		state.callbacks.push(cb);
	} else {
		observedNodes.set(node, {
			rect: undefined,
			callbacks: [cb],
		});
	}

	if (wasEmpty) {
		run();
	}

	return () => {
		const state_ = observedNodes.get(node);
		if (state_) {
			const index = state_.callbacks.indexOf(cb);
			if (index !== -1) {
				state_.callbacks.splice(index, 1);
			}

			if (state_.callbacks.length === 0) {
				observedNodes.delete(node);
			}

			if (observedNodes.size === 0) {
				cancelAnimationFrame(rafId);
			}
		}
	};
}

export function useRect<T extends Element = HTMLElement>(
	nodeRef: RefObject<T | undefined | null>,
	{ observe = true } = {}
): null | DOMRect {
	const [element, setElement] = useState(nodeRef.current);
	const initialRectIsSet = useRef(false);
	const initialRefIsSet = useRef(false);
	const [rect, setRect] = useState<DOMRect | null>(null);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	useLayoutEffect(() => {
		if (nodeRef.current !== element) {
			setElement(nodeRef.current);
		}
	});

	useLayoutEffect(() => {
		if (element && !initialRectIsSet.current) {
			initialRectIsSet.current = true;
			setRect(element.getBoundingClientRect());
		}
	}, [element]);

	useLayoutEffect(() => {
		if (!observe) {
			return;
		}

		let elem = element;
		// State initializes before refs are placed, meaning the element state will
		// be undefined on the first render. We still want the rect on the first
		// render, so initially we'll use the nodeRef that was passed instead of
		// state for our measurements.
		if (!initialRefIsSet.current) {
			initialRefIsSet.current = true;
			elem = nodeRef.current;
		}

		if (!elem) {
			console.warn('You need to place the ref');
			return;
		}

		const unobserve = observeRect(elem, (newRect) => {
			setRect(newRect);
		});
		return () => {
			unobserve();
		};
	}, [observe, element, nodeRef]);

	return rect;
}
